"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authorStudioImportHref, isGoogleFormsUrl } from "@/features/quiz/lib/google-forms-url";

/**
 * The teachers band's wedge: paste a Google Form link, land in the author
 * studio with the import modal prefilled (`?importUrl=`). A signed-out teacher
 * passes through login and comes back to the same URL (`DashboardShell` sends
 * `next`). Validation here mirrors the modal's, so nothing accepted here is
 * refused there.
 */
export function GoogleFormsImportField({
  locale,
  label,
  placeholder,
  submitLabel,
  invalidMessage,
  note,
}: {
  locale: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  invalidMessage: string;
  note: string;
}) {
  const router = useRouter();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!isGoogleFormsUrl(value)) {
      setError(invalidMessage);
      return;
    }
    setError(null);
    router.push(authorStudioImportHref(locale, value));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2" noValidate>
      <label htmlFor="home-gforms-url" className="block text-sm font-semibold text-[rgb(var(--foreground))]">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          id="home-gforms-url"
          type="url"
          inputMode="url"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "home-gforms-url-err" : "home-gforms-url-note"}
          className="h-11 w-full min-w-0 rounded-[var(--radius)] border border-[rgb(var(--border))] bg-white px-4 text-sm text-[rgb(var(--foreground))] outline-none transition-colors sm:flex-1 placeholder:text-[rgb(var(--muted-foreground))] focus:border-primary focus:ring-2 focus:ring-primary/20 aria-[invalid=true]:border-red-400"
        />
        <Button type="submit" className="h-11 shrink-0 px-5 text-sm font-semibold">
          {submitLabel}
          <ArrowRight aria-hidden className="h-4 w-4" />
        </Button>
      </div>
      {error ? (
        <p id="home-gforms-url-err" role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : (
        <p id="home-gforms-url-note" className="text-xs leading-5 text-[rgb(var(--muted-foreground))]">
          {note}
        </p>
      )}
    </form>
  );
}
